// Knowledge Base shared types, label maps and pure helpers (office UI). The DTO shapes mirror the
// server's strict allow-list DTOs (server/src/modules/knowledge/knowledge.ts): they never carry
// tenantId or the Drive storageKey. Kept JSX-free so the pure helpers stay unit-testable and so the
// api client can import the types without pulling in React.

export type KnowledgeStorageSource = "DRIVE" | "GMAIL" | "MANUAL_UPLOAD" | "DRIVE_FOLDER";

export type KnowledgeIngestStatus = "QUEUED" | "PROCESSING" | "COMPLETE" | "FAILED";

export type KnowledgeDocType =
  | "LOLER_REPORT"
  | "SERVICE_RECORD"
  | "TECHNICAL_DRAWING"
  | "FAULT_BULLETIN"
  | "WIRING_DIAGRAM"
  | "MAINTENANCE_MANUAL"
  | "PARTS_LIST"
  | "EMAIL_THREAD"
  | "PREVIOUS_COMPANY_REPORT"
  | "CONDITION_REPORT"
  | "OTHER";

// The six locked pill variants (.ele-pill--*).
export type PillVariant = "green" | "amber" | "red" | "blue" | "purple" | "grey";

// ---- DTOs (JSON over the wire, so dates arrive as ISO strings) ----

export interface KnowledgeDocument {
  id: string;
  fileName: string;
  fileSize: number; // bytes
  mimeType: string;
  storageSource: KnowledgeStorageSource;
  siteId: string | null;
  siteName: string | null;
  assetId: string | null;
  eleNumber: string | null;
  manufacturer: string | null;
  liftModel: string | null;
  docType: KnowledgeDocType;
  faultCategories: string[];
  aiTagsConfirmed: boolean;
  aiTagsSummary: string | null;
  ingestStatus: KnowledgeIngestStatus;
  ingestError: string | null;
  pageCount: number | null;
  // How many of those pages were actually read into the search index. The extractor stops at a fixed
  // cap, so a long manufacturer manual is only partly searchable, and the office must not read
  // "Pages: 120" beside a green Ready pill and conclude the whole document was searched. Null when the
  // page count itself is unknown (a Word or text file has no fixed pages).
  pagesIndexed: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeDocumentDetail extends KnowledgeDocument {
  extractedText: string | null;
  chunkCount: number;
}

// ---- Request/response shapes for the api client ----

export interface KnowledgeTagInput {
  siteId?: string | null;
  assetId?: string | null;
  manufacturer?: string | null;
  liftModel?: string | null;
}

export interface KnowledgeDrivePullInput extends KnowledgeTagInput {
  fileId?: string;
  folderId?: string;
}

export interface KnowledgeGmailPullInput extends KnowledgeTagInput {
  messageId?: string;
  threadId?: string;
}

export interface KnowledgeTagsPatchInput {
  siteId?: string | null;
  assetId?: string | null;
  manufacturer?: string | null;
  liftModel?: string | null;
  docType?: KnowledgeDocType;
  faultCategories?: string[];
  aiTagsConfirmed?: boolean;
}

export interface KnowledgeUploadResult {
  document: KnowledgeDocument | null;
  message: string;
}

export interface KnowledgePullResult {
  created: number;
  skipped: number;
  documents: KnowledgeDocument[];
  message: string;
}

export interface KnowledgeDocumentListQuery {
  siteId?: string;
  assetId?: string;
  manufacturer?: string;
  liftModel?: string;
  docType?: KnowledgeDocType;
  ingestStatus?: KnowledgeIngestStatus;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface KnowledgeDocumentListResult {
  items: KnowledgeDocument[];
  total: number;
  page: number;
  pageSize: number;
}

export interface KnowledgeQueueFailure {
  id: string;
  fileName: string;
  ingestError: string | null;
}

export interface KnowledgeQueueStatus {
  queued: number;
  processing: number;
  complete: number;
  failed: number;
  recentFailures: KnowledgeQueueFailure[];
  statusOrder: string[];
}

export interface KnowledgeQueryContext {
  siteId?: string;
  assetId?: string;
  manufacturer?: string;
  liftModel?: string;
}

export interface KnowledgeQueryInput {
  question: string;
  context?: KnowledgeQueryContext;
  includeV2History?: boolean;
  // Session BBX1: the person's one tap, "Search all makers". Nothing in a widened result is a confirmed match.
  widen?: boolean;
  // The maker the person picked from the picker.
  pickedMaker?: string;
}

export interface KnowledgeQuerySource {
  documentId: string;
  fileName: string;
  docType: string;
  chunkIndex: number;
  manufacturer: string | null;
  liftModel: string | null;
  // Session AFD. The exact passage the assistant read, in full and never truncated, so the answer can
  // be checked against the source wording.
  chunkText: string;
  // The page of the source document, or null when that cannot be established honestly (a document
  // ingested before page markers existed, or a Word/text file, which has no fixed pages).
  pageNumber: number | null;
  // Cosine similarity to the question, 0 to 1. Shown as a match strength, never as a raw number.
  similarityScore: number;
  // The document's AI summary, denormalised onto the source so the engineer's read-only viewer can
  // show it without an office-only document fetch.
  documentSummary: string | null;
  // Session BAV: a named place that is not a numbered page, whether the page is a diagram, the kind of file, whether a
  // picture of the page can be opened, and where the original opens in Drive. Optional so an older server's reply reads.
  pageLabel?: string | null;
  isDiagram?: boolean;
  sourceKind?: string | null;
  hasPageImage?: boolean;
  openOriginalUrl?: string | null;
  // Session BBX1: Exact (the code is defined on this page of this maker) or Likely. Never a percentage. Optional so an
  // older server's reply reads.
  tier?: KnowledgeTier;
  matchedCode?: string | null;
  // A brake release, rescue, isolation or similar page: chunkText is the page as printed, with no summary beside it.
  safety?: boolean;
  modelAmbiguous?: boolean;
  snippet?: string;
  // Session BBX3: how the result came to be, and the printed table rows behind it (a code row, or the symptom rows of this
  // page with every cause line and the remedy, exactly as printed). Optional so an older server's reply reads.
  via?: KnowledgeResultVia;
  rows?: KnowledgeFaultRow[];
}

// Session BBX3: a printed row of a manual's fault code table or troubleshooting table, never rewritten.
export interface KnowledgeFaultRow {
  id: string;
  kind: "code" | "symptom";
  code: string | null;
  symptom: string | null;
  meaning: string | null;
  causeLines: string[];
  remedy: string | null;
  rowText: string;
  safety: boolean;
  source: string;
}

export type KnowledgeResultVia = "passage" | "code_row" | "symptom_rows" | "heading" | "phrase_map";

// Session BBX3: what was typed against what was searched, and the reviewed phrase map's widening.
export interface KnowledgeSpellingCorrection {
  original: string;
  corrected: string;
  changes: Array<{ from: string; to: string }>;
  notice: string;
}

// Session AFD: a source for an answer that came from the web rather than ELE's own documents.
export interface KnowledgeWebSource {
  url: string;
  title: string;
  domain: string;
  snippet: string;
  pageAge: string | null;
}

// Where an answer came from. "needs_context" is not an answer at all: retrieval was too weak and no
// equipment was named, so the asker is prompted for the manufacturer and model and asks again.
export type KnowledgeAnswerSource = "knowledge_base" | "web_search" | "knowledge_base_with_web_supplement" | "needs_context" | "not_found";

// Session BBX1: the three states. Exact and Likely carry results; Not found names the nearest sections.
export type KnowledgeTier = "exact" | "likely";
export type KnowledgeState = "exact" | "likely" | "not_found";

export interface KnowledgeNearestSection {
  documentId: string;
  fileName: string;
  pageNumber: number | null;
  heading: string | null;
  maker: string | null;
}

// Plain English for a tier: words, never a number.
export function tierLabel(tier: KnowledgeTier | undefined): string {
  return tier === "exact" ? "Exact match in the manual" : tier === "likely" ? "Likely match" : "";
}

export function tierPill(tier: KnowledgeTier | undefined): PillVariant {
  return tier === "exact" ? "green" : "blue";
}

export interface KnowledgeQueryResponse {
  answer: string;
  sources: KnowledgeQuerySource[];
  v2HistoryUsed: boolean;
  tokensUsed: number;
  status: "answered" | "needs_context" | "not_found";
  answerSource: KnowledgeAnswerSource;
  webSearchQuery: string | null;
  webSources: KnowledgeWebSource[];
  // Session BBX1. Optional so an older server's reply reads.
  state?: KnowledgeState;
  reason?: string;
  scope?: { maker: string | null; source: string | null; documents: number | null };
  // Makers to choose between when none could be resolved or two were named.
  pickMaker?: string[];
  nearest?: KnowledgeNearestSection[];
  codes?: string[];
  widened?: boolean;
  // Session BBX3: "Showing results for ..." and "Also searched: ..." (null when nothing was changed or widened).
  correction?: KnowledgeSpellingCorrection | null;
  alsoSearched?: string | null;
}

// Session AFD: how strongly a source matched the question, as words rather than a number. An engineer
// needs to know whether to trust a passage, not what its cosine similarity was.
export type MatchStrength = "strong" | "good" | "partial";

export function matchStrength(similarityScore: number): MatchStrength {
  if (similarityScore > 0.8) return "strong";
  if (similarityScore >= 0.65) return "good";
  return "partial";
}

export function matchStrengthLabel(strength: MatchStrength): string {
  return strength === "strong" ? "Strong match" : strength === "good" ? "Good match" : "Partial match";
}

export function matchStrengthPill(strength: MatchStrength): PillVariant {
  return strength === "strong" ? "green" : strength === "good" ? "blue" : "amber";
}

// Shown above a web-search answer. The engineer must know at a glance that this did not come from
// ELE's own records and has not been checked against them.
// Deliberately does NOT say "no documents were found". Retrieval applies no relevance floor, so it
// almost always returns something: the documents were found and were simply too weak to answer from,
// and their ids are recorded against the query. Telling an engineer nothing was found would be false,
// and would also imply ELE holds nothing on that equipment when it may hold plenty.
export const WEB_SEARCH_NOTICE =
  "Nothing in the knowledge base matched this fault closely enough to answer from. The following comes from general technical sources and has not been verified against ELE's own records. Use your professional judgement.";

// Shown when a web answer came back with no sources attached to it. The answer is then the model's
// own general knowledge rather than anything it read, and the engineer is entitled to know that.
export const WEB_SEARCH_NO_SOURCES_NOTICE =
  "The web search returned no sources for this answer, so there is nothing to read further. Treat it as general guidance only.";

// ---- Inline citation markers ---------------------------------------------------------------------

// A piece of an answer: either prose, or a [N] marker that points at a source card.
export type AnswerSegment =
  | { kind: "text"; text: string }
  | { kind: "citation"; label: string; sourceIndex: number };

// Digits are bounded so a stray bracketed number in the prose cannot drive a pathological match.
const CITATION_PATTERN = /\[(\d{1,3})\]/g;

// Split an answer into prose and citation markers.
//
// A marker only becomes a citation if it actually points at a source that exists. The model writes
// these markers, so it can write [5] when three sources were retrieved, or cite at all when the
// answer came from the web and there are no document cards to point at. Every such marker stays
// ORDINARY TEXT, exactly as the model wrote it: nothing is dropped, nothing links to a card that is
// not there, and nothing throws. Pure, so both shells render markers identically.
export function parseAnswerSegments(answer: string, sourceCount: number): AnswerSegment[] {
  const segments: AnswerSegment[] = [];
  if (!answer) return segments;

  let cursor = 0;
  const pushText = (text: string) => {
    if (!text) return;
    const last = segments[segments.length - 1];
    // Merge with the previous run so an out-of-range marker reads as part of the sentence around it.
    if (last && last.kind === "text") last.text += text;
    else segments.push({ kind: "text", text });
  };

  CITATION_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = CITATION_PATTERN.exec(answer)) !== null) {
    const label = match[1] as string;
    const oneBased = Number.parseInt(label, 10);
    const sourceIndex = oneBased - 1;
    const inRange = Number.isFinite(oneBased) && sourceIndex >= 0 && sourceIndex < sourceCount;

    pushText(answer.slice(cursor, match.index));
    if (inRange) segments.push({ kind: "citation", label, sourceIndex });
    else pushText(match[0]);
    cursor = match.index + match[0].length;
  }
  pushText(answer.slice(cursor));
  return segments;
}

// The engineer Fault Assistant response (Session AFC): the office response plus similarSitesCount
// ("recorded on N other lifts in your portfolio"). The office response deliberately omits it.
export interface KnowledgeEngineerQueryResponse extends KnowledgeQueryResponse {
  similarSitesCount: number;
}

// A recent call-out on the lift, for the Fault Assistant's "History on this lift" section (straight
// from V2's own records, not the knowledge base). date is an ISO string; outcome is the resolution
// when recorded, else the raw status.
export interface KnowledgeRecentCallout {
  jobRef: string | null;
  date: string;
  fault: string | null;
  outcome: string;
}

// Lift context for the engineer Fault Assistant: equipment-spec fields only (no customer PII, no
// pricing). model is always null (the Asset model has no distinct model field); ageBand is a
// display band derived from the year of manufacture; recentCallouts is the last few call-outs.
export interface KnowledgeLiftContext {
  assetId: string;
  siteId: string;
  siteName: string | null;
  eleNumber: string | null;
  liftType: string | null;
  manufacturer: string | null;
  model: string | null;
  driveType: string | null;
  assetClass: string;
  yearOfManufacture: number | null;
  ageBand: string | null;
  recentCallouts: KnowledgeRecentCallout[];
}

// The 8 fault categories the engineer may (optionally) pick to focus a query.
export const FAULT_CATEGORIES = [
  "door",
  "controller",
  "motor",
  "hydraulic",
  "electrical",
  "mechanical",
  "safety gear",
  "other",
] as const;
export type FaultCategory = (typeof FAULT_CATEGORIES)[number];

// ---- Office bulk import from a Drive folder (Session AFC) ----

export interface KnowledgeBulkImportInput extends KnowledgeTagInput {
  folder: string; // a Drive folder URL or a bare folder id
}

// The cost preview (reads nothing into the knowledge base). fileCount / willQueue / cost cover only
// the PDFs still TO import; folderTotal is every PDF in the folder and alreadyImported is how many are
// already in the knowledge base (so a re-run of a partly-imported folder is costed honestly).
export interface KnowledgeBulkImportPreview {
  folderId: string;
  folderTotal: number;
  alreadyImported: number;
  fileCount: number; // PDFs still to import (folderTotal minus alreadyImported)
  willQueue: number; // min(fileCount, cap)
  overCap: boolean;
  cap: number;
  perDocTokens: number;
  estimatedTokens: number;
  ratePerMillionUsd: number;
  estimatedCostUsd: number;
  message: string;
}

export interface KnowledgeBulkImportResult {
  created: number;
  skipped: number;
  failed: number;
  capped: boolean;
  remaining: number; // still-pending PDFs held back by the per-run cap (run again to queue them)
  folderTotal: number;
  alreadyImported: number;
  cap: number;
  documents: KnowledgeDocument[];
  message: string;
}

export interface KnowledgeQueryListItem {
  id: string;
  queryText: string;
  queriedBy: string;
  // Session AFD: on the LIST as well as the detail, so the office can scan the history and see which
  // faults the knowledge base could not answer.
  answerSource: KnowledgeAnswerSource;
  askedByName: string | null;
  createdAt: string;
}

export interface KnowledgeQueryListResult {
  items: KnowledgeQueryListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface KnowledgeQueryDetail {
  id: string;
  queryText: string;
  queryContext: unknown;
  retrievedDocIds: string[];
  answerText: string;
  answerModel: string;
  tokensUsed: number | null;
  answerSource: KnowledgeAnswerSource;
  webSearchQuery: string | null;
  queriedBy: string;
  askedByName: string | null;
  createdAt: string;
}

// Plain English for a recorded answer source, for the office query history.
export function answerSourceLabel(source: KnowledgeAnswerSource): string {
  switch (source) {
    case "web_search":
      return "Web search";
    case "knowledge_base_with_web_supplement":
      return "Knowledge base and web";
    case "needs_context":
      return "Asked for lift details";
    case "not_found":
      return "Not found";
    default:
      return "Knowledge base";
  }
}

export function answerSourcePill(source: KnowledgeAnswerSource): PillVariant {
  switch (source) {
    case "web_search":
      return "amber";
    case "needs_context":
    case "not_found":
      return "grey";
    default:
      return "green";
  }
}

// ---- Label maps (plain English for the office) ----

// Ordered so the dropdowns read sensibly (statutory first, then technical, then the catch-all).
export const DOC_TYPES: KnowledgeDocType[] = [
  "LOLER_REPORT",
  "SERVICE_RECORD",
  "CONDITION_REPORT",
  "PREVIOUS_COMPANY_REPORT",
  "FAULT_BULLETIN",
  "TECHNICAL_DRAWING",
  "WIRING_DIAGRAM",
  "MAINTENANCE_MANUAL",
  "PARTS_LIST",
  "EMAIL_THREAD",
  "OTHER",
];

export const DOC_TYPE_LABEL: Record<KnowledgeDocType, string> = {
  LOLER_REPORT: "LOLER report",
  SERVICE_RECORD: "Service record",
  TECHNICAL_DRAWING: "Technical drawing",
  FAULT_BULLETIN: "Fault bulletin",
  WIRING_DIAGRAM: "Wiring diagram",
  MAINTENANCE_MANUAL: "Maintenance manual",
  PARTS_LIST: "Parts list",
  EMAIL_THREAD: "Email thread",
  PREVIOUS_COMPANY_REPORT: "Previous company report",
  CONDITION_REPORT: "Condition report",
  OTHER: "Other",
};

export const INGEST_STATUSES: KnowledgeIngestStatus[] = ["QUEUED", "PROCESSING", "COMPLETE", "FAILED"];

export const INGEST_STATUS_LABEL: Record<KnowledgeIngestStatus, string> = {
  QUEUED: "Queued",
  PROCESSING: "Processing",
  COMPLETE: "Ready",
  FAILED: "Failed",
};

export const STORAGE_SOURCE_LABEL: Record<KnowledgeStorageSource, string> = {
  DRIVE: "Google Drive",
  GMAIL: "Gmail",
  MANUAL_UPLOAD: "Uploaded",
  DRIVE_FOLDER: "Manuals library",
};

// ---- Pill colours ----

// Status pill colour (Director-specified: queued grey, processing amber, complete green, failed red).
export function ingestStatusPill(status: KnowledgeIngestStatus): PillVariant {
  switch (status) {
    case "QUEUED":
      return "grey";
    case "PROCESSING":
      return "amber";
    case "COMPLETE":
      return "green";
    case "FAILED":
      return "red";
  }
}

// Document type reads as technical detail, so it takes the blue pill throughout.
export function docTypePill(): PillVariant {
  return "blue";
}

export function docTypeLabel(t: string): string {
  return (DOC_TYPE_LABEL as Record<string, string>)[t] ?? t;
}

// ---- Pure helpers ----

// A human-readable file size. 0 or a missing size reads "Unknown".
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null || bytes <= 0) return "Unknown";
  const units = ["bytes", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = unit === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[unit]}`;
}

// Extract a Google Drive id from a pasted URL or a bare id. `kind` picks the URL shape to look for
// (a file URL is .../file/d/<id>/..., a folder URL is .../drive/folders/<id>) so the office can paste
// either the share link or just the id. Falls back to the last path segment, which is a bare id.
export function driveIdFromInput(raw: string, kind: "file" | "folder"): string {
  const s = raw.trim();
  if (!s) return "";
  if (kind === "folder") {
    const m = s.match(/\/folders\/([a-zA-Z0-9_-]+)/);
    if (m) return m[1];
  } else {
    const m = s.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (m) return m[1];
  }
  const open = s.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (open) return open[1];
  const seg = s.split(/[/?#]/).filter(Boolean).pop();
  return seg ?? s;
}

// Extract a Gmail message or thread id from a pasted URL or a bare id (the id is the final URL
// segment; a bare id is returned unchanged).
export function gmailIdFromInput(raw: string): string {
  const s = raw.trim();
  if (!s) return "";
  const seg = s.split(/[/#?]/).filter(Boolean).pop();
  return seg ?? s;
}

// A short date for the office lists (e.g. "3 Jul 2026").
export function knowledgeShortDate(iso: string | null | undefined): string {
  if (!iso) return "Not set";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "Not set"
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}


// ---- The Drive manuals library (Session BAV) ----

export type KnowledgeDriveStatus =
  | "QUEUED"
  | "DOWNLOADING"
  | "EXTRACTING"
  | "OCR"
  | "VISION"
  | "INDEXED"
  | "VISION_PAUSED"
  | "SKIPPED"
  | "NEEDS_PASSWORD"
  | "STORED_NOT_SEARCHABLE"
  | "FAILED"
  | "REMOVED";

export const KNOWLEDGE_DRIVE_STATUSES: KnowledgeDriveStatus[] = [
  "QUEUED",
  "DOWNLOADING",
  "EXTRACTING",
  "OCR",
  "VISION",
  "INDEXED",
  "VISION_PAUSED",
  "SKIPPED",
  "NEEDS_PASSWORD",
  "STORED_NOT_SEARCHABLE",
  "FAILED",
  "REMOVED",
];

// The same words the server uses (server/src/modules/knowledge/drive/status.ts); the server also sends statusLabel on
// every row, which the screen shows. This map is for the filter list.
export const KNOWLEDGE_DRIVE_STATUS_LABEL: Record<KnowledgeDriveStatus, string> = {
  QUEUED: "Queued",
  DOWNLOADING: "Downloading",
  EXTRACTING: "Extracting",
  OCR: "Reading scanned pages (OCR)",
  VISION: "Reading diagrams",
  INDEXED: "Indexed",
  VISION_PAUSED: "Vision budget reached",
  SKIPPED: "Skipped",
  NEEDS_PASSWORD: "Needs password",
  STORED_NOT_SEARCHABLE: "Stored, not searchable",
  FAILED: "Failed",
  REMOVED: "Removed from the knowledge base",
};

export function knowledgeDriveStatusPill(status: KnowledgeDriveStatus, sourceMissing: boolean): PillVariant {
  if (sourceMissing) return "amber";
  switch (status) {
    case "INDEXED":
      return "green";
    case "FAILED":
      return "red";
    case "VISION_PAUSED":
    case "NEEDS_PASSWORD":
    case "SKIPPED":
      return "amber";
    case "DOWNLOADING":
    case "EXTRACTING":
    case "OCR":
    case "VISION":
      return "blue";
    case "STORED_NOT_SEARCHABLE":
      return "purple";
    default:
      return "grey";
  }
}

export interface KnowledgeDriveSettings {
  folderId: string | null;
  // Where the folder came from: an admin's setting, the server's environment fallback, or nowhere.
  folderSource: "setting" | "env" | "none";
  folderUpdatedAt: string | null;
  folderUpdatedByName: string | null;
  visionMonthlyPageBudget: number;
  visionBudgetIsDefault: boolean;
  visionPagesUsedThisMonth: number;
  maxFileMb: number;
  maxFileIsDefault: boolean;
  ocrLanguages: string[];
  googleConnected: boolean;
  // Whether the Google connection can read files the platform did not create; null when not connected.
  driveScopeOk: boolean | null;
  // Pages waiting for the vision pass because the month's budget was spent (each is searchable by its OCR text meanwhile),
  // the estimated cost of reading them, and how many months the current budget takes to reach them (null with no budget).
  pagesWaitingForVision: number;
  visionCostPerPageUsd: number;
  visionWaitingCostUsd: number;
  visionMonthsToClear: number | null;
  // The database disk guard: the disk's size (entered here, default 15 GB), the space kept free, the database's size now.
  dbDiskGb: number;
  dbDiskIsDefault: boolean;
  dbMinFreeGb: number;
  dbUsedBytes: number | null;
  dbFreeBytes: number | null;
  dbLibraryPaused: boolean;
  // The worker's temporary working space (Session BBT): what its work directory held, the limit it reads against, and when
  // the worker measured it. All null until the worker has measured it once.
  tmpUsedBytes: number | null;
  tmpLimitBytes: number | null;
  tmpMeasuredAt: string | null;
}

// One name the library files documents under, and the office's correction of it (Session BAV real library run).
export interface KnowledgeLibraryName {
  name: string;
  key: string;
  level: "source" | "inside_group";
  group: string | null;
  isGroup: boolean;
  isGroupByDefault: boolean;
  isGroupSetByOffice: boolean | null;
  renameTo: string | null;
  shownAs: string;
  files: number;
  updatedAt: string | null;
}

// Session BBX2: who made each library document, from its own text.
export interface KnowledgeMakerIdentitySummary {
  byConfidence: Record<string, number>;
  needsReview: number;
  awaitingModelPass: number;
  generalReference: number;
  decided: number;
  withCategory: number;
  modelPass: { documents: number; estimatedUsd: number; withinCap: boolean; model: string };
  message: string;
}

export interface KnowledgeMakerEvidence {
  signal: string;
  page: number | null;
  phrase: string;
  maker: string;
}

export interface KnowledgeMakerReviewItem {
  documentId: string;
  fileName: string;
  folder: string | null;
  category: string | null;
  confidence: string | null;
  evidence: KnowledgeMakerEvidence[];
  proposedMaker: string | null;
  proposedBy: string | null;
  proposedEvidence: KnowledgeMakerEvidence[];
  pageCount: number | null;
  decision: string | null;
}

export interface KnowledgeLibraryNameInput {
  name: string;
  isGroup?: boolean | null;
  renameTo?: string | null;
}

export interface KnowledgeDriveSweepSummary {
  folderId: string | null;
  folders: number;
  files: number;
  added: number;
  changed: number;
  missing: number;
  returned: number;
  queued: number;
  complete: boolean;
  problems: string[];
}

export interface KnowledgeDriveStatusResponse {
  settings: KnowledgeDriveSettings;
  lastSweep: { at: string | null; completeAt: string | null; error: string | null; summary: KnowledgeDriveSweepSummary | null };
  counts: Record<KnowledgeDriveStatus, number>;
  sourceMissing: number;
  // Every skipped file accounted for (Session BBV). Optional so an older server's answer still reads.
  skipped?: KnowledgeDriveSkippedAccounting;
  total: number;
}

// The restore rebuild (Session BDX; the office control is Session SCR1). The two bulk modes, the preview a start must quote, and the run.
export type KnowledgeRebuildMode = "pictures" | "passages";
export interface KnowledgeRebuildPreview {
  ok: boolean;
  preview: true;
  mode: KnowledgeRebuildMode;
  /** The number a start must quote back: pages for pictures, documents for passages. */
  confirmNumber: number;
  confirmWhat: "pages" | "documents";
  nothingToDo: boolean;
  message: string;
}
export interface KnowledgeRebuildStatus {
  pagesMissingPicture: number;
  run: null | {
    mode: KnowledgeRebuildMode;
    state: "running" | "waiting_for_worker" | "interrupted" | "stopped" | "done" | "failed";
    startedAt: string;
    finishedAt: string | null;
    stopRequested: boolean;
    progress: { filesTotal: number; filesDone: number; pagesTotal: number; pagesRecovered: number; pagesFailed: number; documentsTotal: number; documentsDone: number; passagesWritten: number; currentFile: string | null };
    filesFailed: number;
    error: string | null;
  };
}
export interface KnowledgeRebuildStartResult {
  ok: boolean;
  preview: false;
  started?: boolean;
  reason?: string;
  message: string;
}

export interface KnowledgeDriveSkippedFault {
  id: string;
  name: string;
  folder: string;
  problem: "missing" | "removed" | "failed" | "not_yet_indexed" | "stored_not_searchable";
  problemLabel: string;
}

export interface KnowledgeDriveSkippedAccounting {
  total: number;
  categories: Array<{ key: "duplicate" | "lock_stub" | "unsupported" | "too_large" | "other"; label: string; count: number }>;
  otherReasons: Array<{ reason: string; count: number }>;
  duplicates: {
    total: number;
    groups: number;
    originalSearchable: number;
    originalNotSearchable: number;
    fault: boolean;
    faultFiles: KnowledgeDriveSkippedFault[];
    faultFilesShown: number;
    matchRule: string;
    message: string;
  };
}

export interface KnowledgeDriveFileRow {
  id: string;
  name: string;
  // The path inside an archive, for a file found in one; null for a file in Drive itself.
  memberPath: string | null;
  parentId: string | null;
  manufacturer: string;
  folderPath: string;
  family: string | null;
  mimeType: string;
  sizeBytes: number | null;
  status: KnowledgeDriveStatus;
  statusLabel: string;
  statusReason: string | null;
  lastError: string | null;
  sourceMissing: boolean;
  pageCount: number | null;
  pagesDone: number;
  visionPagesUsed: number;
  lastSyncedAt: string | null;
  ingestedAt: string | null;
  documentId: string | null;
  duplicateOfDocumentId: string | null;
  duplicateOfFileName: string | null;
  openOriginalUrl: string;
  canRetry: boolean;
  canReingest: boolean;
  canRemove: boolean;
  canForget: boolean;
}

export type KnowledgeDriveSort = "name" | "manufacturer" | "status" | "size" | "lastSyncedAt";

export interface KnowledgeDriveFileListQuery {
  status?: KnowledgeDriveStatus | "";
  manufacturer?: string;
  family?: string;
  search?: string;
  sourceMissing?: boolean;
  sort?: KnowledgeDriveSort;
  dir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export interface KnowledgeDriveFileListResult {
  items: KnowledgeDriveFileRow[];
  total: number;
  page: number;
  pageSize: number;
  manufacturers: string[];
}

export interface KnowledgeDriveSettingsInput {
  folderId?: string | null;
  visionMonthlyPageBudget?: number | null;
  maxFileMb?: number | null;
  ocrLanguages?: string | null;
  dbDiskGb?: number | null;
  dbMinFreeGb?: number | null;
}

export interface KnowledgeDriveActionResult {
  ok: boolean;
  message: string;
  file?: KnowledgeDriveFileRow;
  count?: number;
}

// One page of a document, for the page viewer (office and the engineer app).
export interface KnowledgePageView {
  documentId: string;
  fileName: string;
  manufacturer: string | null;
  liftModel: string | null;
  sourceKind: string | null;
  pageNumber: number;
  pageCount: number | null;
  label: string | null;
  text: string | null;
  isDiagram: boolean;
  visionDescription: string | null;
  hasImage: boolean;
  imageWidth: number | null;
  imageHeight: number | null;
  prevPage: number | null;
  nextPage: number | null;
  openOriginalUrl: string | null;
}

// A page found by "Find the page" search.
export interface KnowledgePageSearchResult {
  documentId: string;
  chunkId: string;
  fileName: string;
  manufacturer: string | null;
  liftModel: string | null;
  docType: string;
  sourceKind: string | null;
  pageNumber: number | null;
  pageLabel: string | null;
  isDiagram: boolean;
  snippet: string;
  matchedCode: string | null;
  score: number;
  hasPageImage: boolean;
  openOriginalUrl: string | null;
  // Session BBX1 (optional so an older server reads).
  tier?: KnowledgeTier;
  safety?: boolean;
  modelAmbiguous?: boolean;
  via?: KnowledgeResultVia;
  rows?: KnowledgeFaultRow[] | null;
}

export interface KnowledgePageSearchResponse {
  results: KnowledgePageSearchResult[];
  codes: string[];
  state?: KnowledgeState;
  reason?: string;
  scope?: { maker: string | null; source: string | null; documents: number | null };
  pickMaker?: string[];
  nearest?: KnowledgeNearestSection[];
  notFoundText?: string | null;
  widened?: boolean;
  correction?: KnowledgeSpellingCorrection | null;
  alsoSearched?: string | null;
}

// The address of a page's picture. The browser fetches it with the session cookie like any other API call.
export function knowledgePageImageUrl(documentId: string, pageNumber: number): string {
  return `/api/knowledge/documents/${encodeURIComponent(documentId)}/pages/${pageNumber}/image`;
}

// "Page 47", "Sheet: Fault codes, rows 2 to 26", or "" when neither is known.
export function pagePlaceLabel(pageNumber: number | null | undefined, pageLabel?: string | null): string {
  if (pageLabel) return pageLabel;
  return typeof pageNumber === "number" ? `Page ${pageNumber}` : "";
}

// ---- Session BBX3: the vocabulary and the printed fault rows (office page) ----------------------------------------------

export interface KnowledgeVocabularyStatus {
  vocabulary: { total: number; lastRebuiltAt: string | null; byClass: Array<{ scopeClass: string; terms: number }>; byKind: Array<{ kind: string; terms: number }> };
  faultRows: {
    total: number;
    codeRows: number;
    symptomRows: number;
    documents: number;
    byMaker: Array<{ makerKey: string; display: string; codeRows: number; symptomRows: number; documents: number; skippedPages: number }>;
  };
  lastBackfill: { at: string; skippedPages: Array<{ reason: string; label: string; pages: number }> } | null;
  running: { vocabulary: boolean; backfill: boolean };
  // Session BBX4: whether the Likely floor is one a person approved (kb-calibration.json) or the one set in the code.
  calibration?: { calibrated: boolean; message: string; approvedBy: string | null; approvedAt: string | null };
  message: string;
}

export interface KnowledgeFaultRowSample {
  manual: string;
  maker: string | null;
  page: number;
  kind: "code" | "symptom";
  source: string;
  code: string | null;
  symptom: string | null;
  printed: string[];
  safety: boolean;
}

export interface KnowledgeFaultRowsBackfill {
  ok: boolean;
  preview: boolean;
  running?: boolean;
  message: string;
  documents?: number;
  documentsWithRows?: number;
  pagesRead?: number;
  totalRows?: number;
  codeRows?: number;
  symptomRows?: number;
  safetyRows?: number;
  skippedPages?: Array<{ reason: string; label: string; pages: number }>;
  // Session BBX4: the junk filter's counts (rows read, rows rejected per rule) and the documents left out as not manuals.
  rowsBeforeFilter?: number;
  rejectedRows?: number;
  rejectedByRule?: Array<{ rule: string; label: string; rows: number }>;
  // Session BBX4b: every excluded document by name (was ten examples), and the comparison with the last preview.
  excludedDocuments?: Array<{ rule: string; label: string; documents: number; files: Array<{ documentId: string; fileName: string; maker: string | null; driveFileId: string | null; memberPath: string | null }> }>;
  comparedWithLastPreview?: {
    against: string;
    rowsFound: { last: number; now: number };
    rejected: { last: number; now: number };
    kept: { last: number; now: number };
    excludedDocuments: { last: number; now: number };
    byRule: Array<{ rule: string; label: string; last: number; now: number }>;
  } | null;
  byMaker?: Array<{ makerKey: string; display: string; documents: number; codeRows: number; symptomRows: number; skippedPages: number; rowsBeforeFilter?: number; rejectedRows?: number }>;
  samples?: Record<"code" | "symptom", Record<"text" | "ocr" | "vision", KnowledgeFaultRowSample[]>>;
}

export interface KnowledgeFaultRowRecord {
  id: string;
  kind: "code" | "symptom";
  documentId: string;
  fileName: string;
  manufacturer: string | null;
  liftModel: string | null;
  pageNumber: number;
  code: string | null;
  symptom: string | null;
  meaning: string | null;
  causeLines: string[];
  remedy: string | null;
  printed: string[];
  safety: boolean;
  source: string;
}

export interface KnowledgeSuggestion {
  kind: string;
  text: string;
}
